/**
 * Every URL in the app, in one place.
 *
 * Beyond the usual typo-safety, this is what makes the `appRefNo`-in-the-URL
 * decision reversible: putting the reference number in the path gives us
 * refresh-survival, deep-linkable E2E tests and a future "resume" link in an SMS,
 * but it also puts it in browser history and proxy access logs. If VFS security
 * objects, moving to opaque `/apply/step/contact` URLs is a change to this file
 * and the guards, not a change to every component that navigates.
 */
export const paths = {
  home: '/',

  apply: {
    root: '/apply',
    /** Step 1, and the only screen that exists before a reference is minted. */
    idNumber: '/apply/id-number',
    eligibility: (appRefNo: string) => `/apply/${appRefNo}/eligibility`,
    verify: (appRefNo: string) => `/apply/${appRefNo}/verify`,
    unableToVerify: (appRefNo: string) => `/apply/${appRefNo}/unable-to-verify`,
    details: (appRefNo: string) => `/apply/${appRefNo}/details`,
    contact: (appRefNo: string) => `/apply/${appRefNo}/contact`,
    address: (appRefNo: string) => `/apply/${appRefNo}/address`,
    // These two keys are named for their WIZARD_STEPS ids, not for their slugs:
    // stepPath() resolves a step to a URL with `paths.apply[step]`.
    officer: (appRefNo: string) => `/apply/${appRefNo}/officer`,
    physicalVerification: (appRefNo: string) => `/apply/${appRefNo}/physical-verification`,
    declaration: (appRefNo: string) => `/apply/${appRefNo}/declaration`,
    review: (appRefNo: string) => `/apply/${appRefNo}/review`,
    submitted: (appRefNo: string) => `/apply/${appRefNo}/submitted`,
  },

  track: {
    root: '/track',
    status: (appRefNo: string) => `/track/${appRefNo}`,
  },

  static: {
    help: '/help',
    privacy: '/privacy',
    terms: '/terms',
    accessibility: '/accessibility',
  },
} as const

/** Route patterns, as react-router matches them. */
export const routePatterns = {
  apply: {
    root: '/apply',
    idNumber: '/apply/id-number',
    wizard: '/apply/:appRefNo',
    eligibility: 'eligibility',
    verify: 'verify',
    unableToVerify: 'unable-to-verify',
    details: 'details',
    contact: 'contact',
    address: 'address',
    officer: 'officer',
    physicalVerification: 'physical-verification',
    declaration: 'declaration',
    review: 'review',
    submitted: 'submitted',
  },
  track: {
    root: '/track',
    status: '/track/:appRefNo',
  },
  static: {
    help: '/help',
    privacy: '/privacy',
    terms: '/terms',
    accessibility: '/accessibility',
  },
} as const
